import { IconCheckmarkBold } from '@purpur/library/icon/checkmark-bold';
import { IconMinusBold } from '@purpur/library/icon/minus-bold';
import styled, { css } from 'styled-components';

import {
    getPurpurBorder,
    getPurpurColor,
    getPurpurMotion,
    getPurpurSpacing,
    getPurpurTypography,
} from '@telialt/common-theme/src/purpurSelectors';

import { CheckboxPosition, ImageFitVariant } from './model';

const cardSpacing = getPurpurSpacing('200');

export const Indicator = styled.span<{ $checkboxPosition: CheckboxPosition }>(({ $checkboxPosition }) => {
    const $oppositePosition = $checkboxPosition === 'left' ? 'right' : 'left';

    return css`
            grid-area: indicator;
            margin-top: ${cardSpacing};
            margin-${$checkboxPosition}:${cardSpacing};
            margin-${$oppositePosition}:${getPurpurSpacing('150')};
    
            display: flex;
            align-items: center;
            justify-content: center;
            line-height: 1;
            height: ${getPurpurSpacing('300')};
            width: ${getPurpurSpacing('300')};
            background-color: ${getPurpurColor('backgroundPrimary')};
            border-radius: ${getPurpurBorder('radiusXs')};
            box-shadow: ${getPurpurColor('borderInteractiveSubtle')} 0 0 0 ${getPurpurBorder('widthXs')} inset;
            transition:
                box-shadow ${getPurpurMotion('duration150')} ease,
                background-color ${getPurpurMotion('duration150')} ease;
        `;
});
export const CheckedSymbol = styled(IconCheckmarkBold).attrs({ size: 'xs' })`
    display: none;
`;
export const IndeterminateSymbol = styled(IconMinusBold).attrs({ size: 'xs' })`
    display: none;
`;

export const Card = styled.div<{ $checkboxPosition: CheckboxPosition }>(
    ({ $checkboxPosition }) => css`
        position: relative;

        [hidden] {
            display: none;
        }

        label {
            all: unset;
            overflow: hidden;
            background-color: ${getPurpurColor('backgroundPrimary')};
            border-radius: ${getPurpurBorder('radiusMd')};
            display: grid;
            ${($checkboxPosition === 'left' &&
                css`
                    grid-template: 'indicator main image' auto / auto 1fr auto;
                `) ||
            ($checkboxPosition === 'right' &&
                css`
                    grid-template: 'image main indicator' auto / auto 1fr auto;
                `)};

            &:after {
                content: '';
                inset: 0;
                width: 100%;
                height: 100%;
                position: absolute;
                pointer-events: none;
                border-radius: ${getPurpurBorder('radiusMd')};
                transition: box-shadow ${getPurpurMotion('duration150')} ease;
                box-shadow: ${getPurpurColor('borderInteractiveSubtle')} 0 0 0 ${getPurpurBorder('widthXs')} inset;
            }
            &:hover:after {
                box-shadow: ${getPurpurColor('borderInteractiveSubtleHover')} 0 0 0 ${getPurpurBorder('widthSm')} inset;
            }
            &:active:after {
                box-shadow: ${getPurpurColor('borderInteractiveSubtleHover')} 0 0 0 ${getPurpurBorder('widthXs')} inset;
            }
        }

        input {
            opacity: 0;
            position: absolute;
            margin: 0;
        }
        input:indeterminate {
            ~ label ${Indicator} ${IndeterminateSymbol} {
                display: block;
            }
        }
        input:checked {
            ~ label {
                &:after {
                    box-shadow: ${getPurpurColor('borderInteractivePrimary')} 0 0 0 ${getPurpurBorder('widthSm')} inset;
                }
                &:hover:after {
                }
                &:active:after {
                    box-shadow: ${getPurpurColor('borderInteractivePrimary')} 0 0 0 ${getPurpurBorder('widthXs')} inset;
                }
            }

            ~ label ${Indicator} {
                box-shadow: none;
                background-color: ${getPurpurColor('backgroundInteractivePrimary')};
                color: ${getPurpurColor('functionalWhite')};
            }
            ~ label ${Indicator} ${CheckedSymbol} {
                display: block;
            }
        }

        input[readonly] {
            pointer-events: none;
            ~ label {
                pointer-events: none;
                cursor: not-allowed;
                &:after {
                    box-shadow: ${getPurpurColor('borderInteractiveSubtle')} 0 0 0 ${getPurpurBorder('widthXs')} inset;
                }
                &:hover:after {
                    box-shadow: ${getPurpurColor('borderInteractiveSubtle')} 0 0 0 ${getPurpurBorder('widthXs')} inset;
                }
                &:active:after {
                    box-shadow: ${getPurpurColor('borderInteractiveSubtle')} 0 0 0 ${getPurpurBorder('widthXs')} inset;
                }
            }

            ~ label ${Indicator} {
                cursor: not-allowed;
                box-shadow: ${getPurpurColor('borderWeak')} 0 0 0 ${getPurpurBorder('widthXs')} inset;
                background-color: ${getPurpurColor('backgroundInteractiveDisabled')};
            }
        }

        input:disabled {
            ~ label {
                background-color: ${getPurpurColor('backgroundInteractiveDisabled')};
                cursor: not-allowed;
                &:after {
                    box-shadow: ${getPurpurColor('borderMedium')} 0 0 0 ${getPurpurBorder('widthXs')} inset;
                }
                &:hover {
                    border-color: ${getPurpurColor('borderWeak')};
                }
            }
            ~ label ${Indicator} {
                box-shadow: ${getPurpurColor('borderWeak')} 0 0 0 ${getPurpurBorder('widthXs')} inset;
                background-color: ${getPurpurColor('backgroundInteractiveDisabled')};
                cursor: not-allowed;
            }
        }
        input:disabled:checked {
            ~ label {
                border-color: ${getPurpurColor('borderWeak')};
                &:hover {
                }
            }
            ~ label ${Indicator} {
                color: ${getPurpurColor('textWeak')};
            }
        }

        input:focus:not(:focus-visible) {
            outline: 0;
        }
        input:focus:focus-visible ~ label::before {
            content: '';
            position: absolute;
            inset: calc(-1 * calc((${getPurpurBorder('widthSm')} * 2) + ${getPurpurBorder('widthXs')}));
            border-radius: ${getPurpurSpacing('150')};
            border: ${getPurpurBorder('widthSm')} solid ${getPurpurColor('borderInteractiveFocus')};
        }
    `,
);

export const Image = styled.img<{ $imageFit?: ImageFitVariant; $checkboxPosition: CheckboxPosition }>(
    ({ $imageFit = 'cover', $checkboxPosition }) => {
        const $oppositePosition = $checkboxPosition === 'left' ? 'right' : 'left';

        return css`
            grid-area: image;
            width: ${getPurpurSpacing('1000')};
            height: 100%;
            object-fit: cover;

            ${($imageFit === 'contain' &&
                css`
                    max-height: ${getPurpurSpacing('1000')};
                    margin-top: ${cardSpacing};
                    margin-${$checkboxPosition}:${getPurpurSpacing('200')};
                    margin-${$oppositePosition}:${cardSpacing};
                `) ||
            ($imageFit === 'outset' &&
                css`
                    margin-top: 0;
                    margin-${$checkboxPosition}:${getPurpurSpacing('200')};
                    margin-${$oppositePosition}:0;
                `)};
        `;
    },
);

export const Main = styled.div<{ $checkboxPosition: CheckboxPosition }>(({ $checkboxPosition }) => {
    const $oppositePosition = $checkboxPosition === 'left' ? 'right' : 'left';

    return css`
        grid-area: main;
        margin-top: ${cardSpacing};
        margin-bottom: ${cardSpacing};
        margin-${$oppositePosition}: ${cardSpacing};
        ${Image} ~ & {
            margin-${$oppositePosition}: 0;
        }
    `;
});

const Text = styled.div`
    hyphens: none;
    color: ${getPurpurColor('textDefault')};
    font-family: ${getPurpurTypography('familyDefault')};
    font-size: ${getPurpurTypography('scale100')};
    line-height: ${getPurpurTypography('lineHeightLoose')};
    font-weight: ${getPurpurTypography('weightNormal')};
`;

export const Title = styled(Text)`
    font-weight: ${getPurpurTypography('weightBold')};
`;
export const Body = styled(Text)`
    margin-top: ${getPurpurSpacing('50')};
`;
